import React, { useEffect, useRef, useState } from 'react';
import './index.css';
import SimulationForm from './components/SimulationForm';
import SimulationResults from './components/SimulationResults';
import AdBanner from './components/AdBanner';
import AdSenseSlot from './components/AdSenseSlot';
import { getStoredConsent } from './components/ConsentBanner';
import { simulationApi } from './api/simulation';
import { FormData, SimulationResponse, SimulationRequest } from './types/simulation';
import { trackEvent } from './lib/analytics';
import { Link, Route, Routes } from 'react-router-dom';
import ColumnsPage from './pages/ColumnsPage';
import ArticlePage from './pages/ArticlePage';

function App() {
  const [results, setResults] = useState<SimulationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFormData, setLastFormData] = useState<FormData | null>(null);
  const [showAd, setShowAd] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  // 実行回数のトラッキング（localStorage）と広告表示判定
  useEffect(() => {
    const key = 'simulation_run_count';
    const countStr = localStorage.getItem(key);
    const count = countStr ? Number(countStr) : 0;
    // 初回は非表示、2回目以降で表示（同意がある場合のみ）
    const consent = getStoredConsent();
    setShowAd(consent === 'granted' && count >= 1);
  }, []);

  const handleSimulation = async (formData: FormData) => {
    setIsLoading(true);
    setError(null);
    setLastFormData(formData);

    try {
      const apiRequest: SimulationRequest = {
        initial_investment: (Number(formData.initialInvestment) || 0) * 10000,
        monthly_investment: (Number(formData.monthlyInvestment) || 0) * 10000,
        years: Number(formData.years) || 0,
        annual_return: (Number(formData.annualReturn) || 0) / 100, // ％ → 小数
        annual_stddev: (Number(formData.annualStddev) || 0) / 100, // ％ → 小数
        trials: Number(formData.trials) || 0,
        // 取り崩し
        decumulation_type: formData.decumulationType || 'none',
        decumulation_start_year: formData.decumulationStartYear === '' ? null : Number(formData.decumulationStartYear),
        withdrawal_fixed_amount: (Number(formData.withdrawalFixedAmount) || 0) * 10000,
        withdrawal_fixed_rate_annual: (Number(formData.withdrawalFixedRateAnnual) || 0) / 100, // ％ → 小数
        // 年金
        pension_monthly_amount: (Number(formData.pensionMonthlyAmount) || 0) * 10000,
        pension_start_year: formData.pensionStartYear === '' ? null : Number(formData.pensionStartYear),
        pension_end_year: formData.pensionEndYear === '' ? null : Number(formData.pensionEndYear),
      };

      const response = await simulationApi.runSimulation(apiRequest);
      setResults(response);
      setIsFormCollapsed(true);
      requestAnimationFrame(() => {
        // 画面上端と結果ボックス上端を揃える
        const el = resultsRef.current;
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 16; // 少し余白
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
      // 解析イベント: シミュレーション実行
      trackEvent('simulation_run', {
        years: apiRequest.years,
        trials: apiRequest.trials,
        initial_investment_man: Math.round(apiRequest.initial_investment / 10000),
        monthly_investment_man: Math.round(apiRequest.monthly_investment / 10000),
        annual_return_pct: (Number(formData.annualReturn) || 0),
        annual_stddev_pct: (Number(formData.annualStddev) || 0),
      });
      // 実行回数をカウントアップし、2回目以降の広告表示に利用
      const key = 'simulation_run_count';
      const countStr = localStorage.getItem(key);
      const count = countStr ? Number(countStr) : 0;
      localStorage.setItem(key, String(count + 1));
      const consent = getStoredConsent();
      if (consent === 'granted' && count + 1 >= 2) setShowAd(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期しないエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const resetSimulation = () => {
    setResults(null);
    setError(null);
    setLastFormData(null);
    setIsFormCollapsed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">
              📊 MIRAIシミュレーター
            </h1>
            <nav className="text-sm text-gray-600 flex gap-4">
              <Link to="/">ホーム</Link>
              <Link to="/columns">投資コラム</Link>
            </nav>
          </div>
        </div>
      </header>

      <Routes>
        <Route
          path="/"
          element={
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
              {/* 上部：入力フォーム */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-800">検索条件</h2>
                </div>
                <div className={`relative pb-4`}>
                  {/* 本体（折りたたみ） */}
                  <div className={isFormCollapsed ? 'hidden' : ''}>
                    <SimulationForm onSubmit={handleSimulation} isLoading={isLoading} compact={true} />
                  </div>
                  {/* 条件タブ（下向き・ボックスと一体化） */}
                  <div className="flex justify-end mt-0">
                    <button
                      type="button"
                      onClick={() => setIsFormCollapsed(v => !v)}
                      className="px-3 py-1 rounded-b-md bg-white text-gray-700 text-xs font-medium border border-gray-100 border-t-0"
                      aria-label={isFormCollapsed ? '条件を表示' : '条件を隠す'}
                    >
                      {isFormCollapsed ? '▼ 条件を表示' : '▼ 条件を隠す'}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">エラーが発生しました</h3>
                        <div className="mt-2 text-sm text-red-700">{error}</div>
                      </div>
                    </div>
                  </div>
                )}
                {showAd && (
                  <>
                    <AdBanner onClose={() => setShowAd(false)} />
                    {/* 追加のA8枠は削除し、過剰表示を回避 */}
                    {process.env.REACT_APP_ADSENSE_CLIENT && process.env.REACT_APP_ADSENSE_SLOT && (
                      <AdSenseSlot className="mt-4" slot={process.env.REACT_APP_ADSENSE_SLOT as string} />
                    )}
                  </>
                )}
              </div>

              {/* 下部：結果表示 */}
              <div ref={resultsRef}>
                {results && lastFormData ? (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-800">シミュレーション結果</h2>
                      <button onClick={resetSimulation} className="btn-secondary text-sm">🔄 新しいシミュレーション</button>
                    </div>
                    <SimulationResults
                      results={results}
                      monthlyInvestment={lastFormData.monthlyInvestment === '' ? 0 : Number(lastFormData.monthlyInvestment)}
                      years={lastFormData.years === '' ? 0 : Number(lastFormData.years)}
                      initialInvestment={lastFormData.initialInvestment === '' ? 0 : Number(lastFormData.initialInvestment)}
                    />
                    {/* 説明を結果の下に表示 */}
                    <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-blue-800 mb-2">💡 シミュレーションについて</h3>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>・モンテカルロ法により、多数のランダムなシナリオで将来の資産額を推定</li>
                        <li>・結果は確率分布として表示され、様々な可能性を確認できます</li>
                        <li>・過去のデータに基づく推定であり、将来の結果を保証するものではありません</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="card h-40 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <h3 className="text-lg font-medium mb-2">上部のフォームで条件を入力して実行してください</h3>
                    </div>
                  </div>
                )}
              </div>
            </main>
          }
        />
        <Route path="/columns" element={<ColumnsPage />} />
        <Route path="/columns/:slug" element={<ArticlePage />} />
      </Routes>


      {/* フッター */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p className="mb-2">MIRAIシミュレーター v1.0</p>
            <p>※ このシミュレーション結果は予測であり、実際の投資成果を保証するものではありません。</p>
            <p>投資は自己責任で行い、リスクを十分に理解した上で実行してください。</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 